import * as React from 'react';
import { Dialog, DialogContent, IconButton, Box, Button, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { ACCEPT, ALLOWED_TYPES, readFileAsDataUrl } from './fileUtils';

interface SelectBackgroundDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (dataUrl: string) => void;
}

export function SelectBackgroundDialog({ open, onClose, onSelect }: SelectBackgroundDialogProps) {
  const theme = useTheme();
  const w = (theme as { weha?: Record<string, unknown> }).weha || {};
  const wehaBlue = (w.primary as string) || theme.palette.primary.main;
  const appFont = theme.typography?.fontFamily || '"Open Sans", "Helvetica Neue", Arial, sans-serif';
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        return;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        onSelect(dataUrl);
      } catch (err) {
        console.error('Failed to read image', err);
      }
      e.target.value = '';
    },
    [onSelect]
  );

  const handleSelectClick = () => {
    inputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        <Box
          component="div"
          style={{
            margin: 0,
            padding: '16px 24px',
            backgroundColor: wehaBlue,
            fontFamily: appFont,
            fontSize: '1rem',
            fontWeight: 600,
            position: 'relative',
            width: '100%',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#fff', fontFamily: appFont }}>
            {t('weha.previewOnStone.selectBackgroundTitle')}
          </span>
          <IconButton
            aria-label={t('weha.previewOnStone.close')}
            onClick={onClose}
            size="small"
            style={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            ×
          </IconButton>
        </Box>
        <Box style={{ padding: 24 }}>
          <Typography
            variant="body2"
            style={{
              color: (w.textMuted as string) || theme.palette.text.secondary,
              marginBottom: 16,
            }}
          >
            {t('weha.previewOnStone.selectBackgroundHint')}
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={onClose}
              style={{
                borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
                marginRight: 16,
              }}
            >
              {t('weha.previewOnStone.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSelectClick}
              style={{ borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius }}
            >
              {t('weha.previewOnStone.selectImage')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
